import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  useTheme,
  styled,
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  FormatPaint as FormatPaintIcon,
  Speed as SpeedIcon,
  QueryBuilder as QueryBuilderIcon,
  Bolt as BoltIcon,
  Security as SecurityIcon,
  Psychology as PsychologyIcon,
  Church as ChurchIcon,
  Favorite as FavoriteIcon,
  EmojiObjects as EmojiObjectsIcon,
  DeveloperMode as DeveloperModeIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { REGISTER_DISABLED } from '../config';

// Styled components
const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
  },
}));

const GradientBox = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: 'white',
  padding: theme.spacing(10, 0),
  marginBottom: theme.spacing(6),
}));

const ShowcasePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const aiFeatures = [
    {
      icon: <SmartToyIcon />,
      title: 'Natural Language Processing',
      description: 'Our AI understands context and nuance, providing human-like conversation experiences.',
    },
    {
      icon: <PsychologyIcon />,
      title: 'Contextual Memory',
      description: 'Chatbot remembers conversation history to deliver more relevant responses.',
    },
    {
      icon: <EmojiObjectsIcon />,
      title: 'Smart Suggestions',
      description: 'AI-powered quick replies guide users to frequently requested information.',
    },
    {
      icon: <BoltIcon />,
      title: 'Adaptive Learning',
      description: 'The system continually improves through interaction and feedback.',
    },
  ];

  const customizationFeatures = [
    {
      icon: <FormatPaintIcon />,
      title: 'Visual Customization',
      description: 'Personalize colors, position, widget shape, and icon styles to match your brand identity.',
    },
    {
      icon: <DeveloperModeIcon />,
      title: 'Keyword Response System',
      description: 'Create tailored responses to specific keywords with variable support and follow-up questions.',
    },
    {
      icon: <QueryBuilderIcon />,
      title: 'Business Hours Integration',
      description: 'Set operating hours with timezone support and custom messaging for after-hours inquiries.',
    },
    {
      icon: <CodeIcon />,
      title: 'Easy Integration',
      description: 'Simple embed code lets you add the chatbot to any website with minimal technical knowledge.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero Section */}
      <GradientBox>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
                AI-Powered Customer Support Made Simple
              </Typography>
              <Typography variant="h6" paragraph>
                Elevate your customer experience with Glowspirit's customizable AI chatbot solution
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 3, fontStyle: 'italic' }}>
                Built on faith, powered by AI
              </Typography>
              {REGISTER_DISABLED ? (
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled
                  sx={{
                    mt: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 'bold',
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                  }}
                >
                  Get Started
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={() => navigate('/register')}
                  sx={{
                    mt: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 'bold',
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  Start Free Trial
                </Button>
              )}
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper
                elevation={10}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(30, 30, 30, 0.9)' 
                    : 'rgba(255, 255, 255, 0.9)',
                  color: 'text.primary',
                }}
              >
                <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                  Trusted by Businesses
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <SpeedIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="24/7 Automated Customer Support" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <SecurityIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Secure & Privacy-Focused" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ChurchIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Christian-Owned Business" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <FavoriteIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Majority of Profits Support Charities" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </GradientBox>

      {/* AI Capabilities Section */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Box textAlign="center" mb={6}>
          <Typography variant="overline" color="primary" fontWeight="bold">
            ADVANCED AI TECHNOLOGY
          </Typography>
          <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
            Intelligent Conversation Engine
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Our chatbot leverages cutting-edge AI to understand customer questions and deliver accurate, helpful responses.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {aiFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <FeatureCard>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ color: 'primary.main', fontSize: 50, mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Customization Section */}
      <Box sx={{ 
        bgcolor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', 
        py: 10 
      }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="overline" color="primary" fontWeight="bold">
              TAILOR TO YOUR BRAND
            </Typography>
            <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
              Extensive Customization Options
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Make the chatbot truly yours with comprehensive personalization features that reflect your brand identity.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {customizationFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <FeatureCard>
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                    <Box sx={{ color: 'primary.main', fontSize: 50, mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Mission & Values Section */}
      <Container maxWidth="md" sx={{ my: 10 }}>
        <Paper elevation={3} sx={{ 
          p: 5, 
          borderRadius: 2,
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
        }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4} textAlign="center">
              <ChurchIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" color="primary" fontWeight="bold">
                Our Mission
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="h6" paragraph>
                As a Christian-owned business, we're committed to excellence and integrity in everything we do.
              </Typography>
              <Typography variant="body1" paragraph>
                Glowspirit was created with the belief that technology should serve with light, love and purpose. That's why the majority of our profits support charitable causes around the world.
              </Typography>
              <Typography variant="body1">
                When you choose Glowspirit, you're not just improving your business — you're helping make the world a better place.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Continuous Development Section */}
      <Box sx={{ 
        bgcolor: theme.palette.mode === 'dark' ? '#1565c0' : 'primary.main', 
        backgroundImage: theme.palette.mode === 'dark' 
          ? 'linear-gradient(to right, #1565c0, #0d47a1)'
          : 'linear-gradient(to right, #1976d2, #1565c0)',
        color: 'white', 
        py: 10,
        borderRadius: theme.shape.borderRadius,
        mx: { xs: 1, sm: 2, md: 4 },
        mb: 4,
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 8px 32px rgba(0, 0, 0, 0.5)' 
          : '0 8px 32px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: theme.palette.mode === 'dark' 
            ? 'radial-gradient(circle at top right, rgba(66, 165, 245, 0.15), transparent 70%)'
            : 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.1), transparent 70%)',
          zIndex: 1,
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Always Evolving, Always Improving
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', opacity: 0.9, mb: 3 }}>
                Our development team is constantly enhancing the platform with new features and capabilities. Your subscription includes access to all future updates at no additional cost.
              </Typography>
              <List>
                <ListItem sx={{ 
                  pl: 0, 
                  '&:hover': { 
                    transform: 'translateX(5px)',
                    transition: 'transform 0.3s'
                  } 
                }}>
                  <ListItemIcon>
                    <BoltIcon sx={{ color: '#90caf9', fontSize: 28 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Regular feature updates" 
                    primaryTypographyProps={{ 
                      fontSize: '1.1rem', 
                      fontWeight: 500 
                    }} 
                    sx={{ color: 'white' }} 
                  />
                </ListItem>
                <ListItem sx={{ 
                  pl: 0, 
                  '&:hover': { 
                    transform: 'translateX(5px)',
                    transition: 'transform 0.3s'
                  } 
                }}>
                  <ListItemIcon>
                    <DeveloperModeIcon sx={{ color: '#90caf9', fontSize: 28 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Expanding AI capabilities" 
                    primaryTypographyProps={{ 
                      fontSize: '1.1rem', 
                      fontWeight: 500 
                    }} 
                    sx={{ color: 'white' }} 
                  />
                </ListItem>
                <ListItem sx={{ 
                  pl: 0, 
                  '&:hover': { 
                    transform: 'translateX(5px)',
                    transition: 'transform 0.3s'
                  } 
                }}>
                  <ListItemIcon>
                    <SecurityIcon sx={{ color: '#90caf9', fontSize: 28 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Ongoing security enhancements" 
                    primaryTypographyProps={{ 
                      fontSize: '1.1rem', 
                      fontWeight: 500 
                    }} 
                    sx={{ color: 'white' }} 
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={5} textAlign="center">
              <Box 
                sx={{ 
                  p: 3, 
                  borderRadius: 3, 
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  disabled={REGISTER_DISABLED}
                  sx={{
                    px: 4,
                    py: 2,
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    bgcolor: 'white',
                    color: '#1565c0',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                      transition: 'all 0.3s'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  Start Your 14-Day Free Trial
                </Button>
                <Typography variant="body1" sx={{ mt: 2, opacity: 0.9 }}>
                  Cancel anytime during your trial period
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ my: 10, textAlign: 'center' }}>
        <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
          Ready to Transform Your Customer Service?
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph sx={{ maxWidth: 700, mx: 'auto', mb: 4 }}>
          Join businesses that have increased customer satisfaction and reduced support costs with Glowspirit's AI chatbot.
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          <Grid item>
            {REGISTER_DISABLED ? (
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled
                sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}
              >
                Start Free Trial
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate('/register')}
                sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}
              >
                Start Free Trial
              </Button>
            )}
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}
            >
              Contact Sales
            </Button>
          </Grid>
        </Grid>
      </Container>

      <Footer />
    </Box>
  );
};

export default ShowcasePage; 